import React, { ReactElement, useRef, useState } from 'react';

import ReviewItemDesktop from 'components/review/review-item.desktop';
import ReviewRatingFilter from 'components/review/review-filter/review-rating-filter';
import ReviewListBlank from 'components/review/review-list-blank/review-list-blank';
import ReviewListSorting from 'components/review/sorting/sorting';
import ReviewListPagination from 'components/review/pagination/pagination';
import ReviewListError from 'components/review/error/review-list-error';
import Loading from 'atomic/loading/loading';
import AdSlot from 'components/ads/ad-slot';
import { SLOT_FIRST_ROW, SLOT_SECOND_ROW } from 'components/ads/ad-slot/ad-defs';
import type { ReviewListProps } from 'components/review/review.interface';
import type { ReviewInterface, ReviewListFilter } from 'modules/review/review.interface';
import { ApiListInterface, ApiSortingDirection } from 'interfaces/api.interface';
import { useFetch } from 'hooks/use-fetch';
import { initialReviewListParams } from 'components/review/initial-params.helper';
import { CompanyReviewSortingFields } from 'interfaces/company-review.interface';
import { ProductReviewSortingFields } from 'interfaces/product-review.interface';

import './review-list.scss';

const ReviewListDesktop: React.FC<ReviewListProps> = (
    {
        items,
        count,
        company,
        params: queryParams,
        objectType,
        type,
        url,
        ratingStats,
        isVisible = false
    }
): ReactElement => {
    const params = useRef<ReviewListFilter>(initialReviewListParams(queryParams));
    const container = useRef<HTMLDivElement>(null);
    const { get, loading } = useFetch(url, { method: 'GET' });

    const [filtered, setFiltered] = useState(items);
    const [totalCount, setTotalCount] = useState(count);
    const [currentOffset, setCurrentOffset] = useState(params.current.offset || 0);
    const [error, setError] = useState(false);

    const handleChangeFilter = (changedParams: ReviewListFilter): void => {
        if (typeof changedParams.rating !== 'undefined') {
            params.current.rating = changedParams.rating;
            params.current.offset = 0;
            setCurrentOffset(0);
        }

        if (changedParams.sortingField) {
            params.current.sortingField = changedParams.sortingField;
            params.current.sortingDirection = changedParams.sortingDirection;
            params.current.offset = 0;
            setCurrentOffset(0);
        }

        if (typeof changedParams.offset !== 'undefined') {
            if (container?.current) {
                window.scrollTo(0, container.current.offsetTop - 150);
            }

            params.current.offset = changedParams.offset;
            params.current.limit = changedParams.limit;
            setCurrentOffset(changedParams.offset);
        }

        get(params.current)
            .then((response: ApiListInterface<ReviewInterface>) => {
                setFiltered(response.items);
                setTotalCount(response.totalCount || 0);
                setError(false);
            })
            .catch(() => {
                setFiltered([]);
                setTotalCount(0);
                setError(true);
            });
    };

    const sortingCreationDateField = type === 'company' ? CompanyReviewSortingFields.creationDate : ProductReviewSortingFields.creationDate;
    const sortingRatingField = type === 'company' ? CompanyReviewSortingFields.rating : ProductReviewSortingFields.rating;

    return (
        <div className="review-list-container" ref={container}>
            {loading && <Loading />}
            <div className="review-list__filters">
                <ReviewRatingFilter
                    type={type}
                    handleFilter={handleChangeFilter}
                    activeFilter={queryParams.rating}
                    ratingStats={ratingStats}
                />
                <ReviewListSorting
                    type={type}
                    handleSorting={handleChangeFilter}
                    currentValue={`${params.current.sortingField}:${params.current.sortingDirection}`}
                    options={[
                        { value: `${sortingCreationDateField}:${ApiSortingDirection.desc}`, title: 'daty dodania - od najnowszej' },
                        { value: `${sortingCreationDateField}:${ApiSortingDirection.asc}`, title: 'daty dodania - od najstarszej' },
                        { value: `${sortingRatingField}:${ApiSortingDirection.desc}`, title: 'oceny - malejąco' },
                        { value: `${sortingRatingField}:${ApiSortingDirection.asc}`, title: 'oceny - rosnąco' }
                    ]}
                />
            </div>
            {
                filtered?.length
                    ? (
                        <>
                            <ul className="review-list">
                                {filtered.map((item, index) => (
                                    <React.Fragment key={item.id}>
                                        <ReviewItemDesktop review={item} company={company} type={type} />
                                        {index === 0 && (
                                            <li className="review-item review-item--blank">
                                                {isVisible && <AdSlot name={SLOT_FIRST_ROW} slotId="8365444921" injectAdsenseOnEmpty={true} />}
                                            </li>
                                        )}
                                        {index === 3 && (
                                            <li className="review-item review-item--blank">
                                                {isVisible && <AdSlot name={SLOT_SECOND_ROW} injectAdsenseOnEmpty={true} />}
                                            </li>
                                        )}
                                    </React.Fragment>
                                ))}
                            </ul>
                            <ReviewListPagination
                                mode="desktop"
                                pagination={queryParams.pagination}
                                currentOffset={currentOffset}
                                totalCount={totalCount}
                                type={type}
                                handleChangePage={handleChangeFilter}
                            />
                        </>
                    )
                    : (
                        error ? <ReviewListError /> : <ReviewListBlank objectType={objectType} isReviewable={company.isReviewable} />
                    )
            }
        </div>
    );
};

export default ReviewListDesktop;
